/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

import _ from 'underscore';
import $ from 'jquery'
import React, { RefObject } from 'react';
import { connect } from 'react-redux';
import { SampleDataServiceComponent } from './SampleDataServiceComponent';
import { MultiLevelDataServiceComponent } from './MultiLevelDataServiceComponent';
import datasetTypes from '../enums/datasetTypes';
import { IState } from '../store/types/StateTypes';
import { TitleComponent } from './TitleComponent';
import { MultiAxisDataServiceComponent } from './MultiAxisDataServiceComponent';
import AdHocMessageComponent from './AdHocMessageComponent';
import {
    changeSelectedGroup,
    changeSelectedVisualizationType,
    setVisualizationChooserTabCollapsed,
    setVisualizationChooserVisibility
} from '../actions/visualizationChooserActions';
// @ts-ignore
import VisualChooserComponent from '../api/visualChooser/components/VisualChooserComponent';
import IVisualizationChooser from '../store/types/VisualizationChooser';
import i18n from '../../i18n/AdHocBundle.properties';

import adhocSettings from '../adhocSettings';
import { resize } from '../actions/apiActions';

interface ILayoutComponentProperties {
    autoresize: boolean,
    adHocViewHeight: number;
    isInDesignerMode: boolean;
    visualizationDescriptor: any;
    levelsLayout: any;
    deferredAction: JQuery.Deferred<any>;
    visualizationChooser: IVisualizationChooser,
    setVisualizationChooserVisibility: Function;
    setVisualizationChooserTabCollapsed: Function;
    changeSelectedVisualizationType: Function;
    changeSelectedGroup: Function;
    resize: Function;
    eventCallbacks: any;
    titleOptions: any;
    visualizationTypesRegistry: any;
}

const mapStateToProps = (state: IState) => ({
    autoresize: state.autoresize,
    adHocViewHeight: state.adHocViewHeight,
    isInDesignerMode: state.isInDesignerMode,
    visualizationDescriptor: state.visualizationDescriptor,
    visualizationTypesRegistry: state.visualizationTypesRegistry,
    levelsLayout: state.levelsLayout,
    visualizationChooser: state.visualizationChooser,
    deferredAction: state.deferredAction,
    eventCallbacks: state.eventCallbacks,
    titleOptions: state.titleOptions
});

const mapDispatchToProps = (dispatch: Function) => ({
    setVisualizationChooserVisibility: (visible: boolean) => {
        dispatch(setVisualizationChooserVisibility(visible));
    },
    setVisualizationChooserTabCollapsed: (isCollapsed: boolean) => {
        dispatch(setVisualizationChooserTabCollapsed(isCollapsed));
    },
    changeSelectedGroup: (groupName: string) => {
        dispatch(changeSelectedGroup(groupName));
    },
    changeSelectedVisualizationType: (type: any) => {
        dispatch(changeSelectedVisualizationType(type));
    },
    resize: (height: number, width: number) => {
        dispatch(resize(height, width));
    }
});

class LayoutComponentUnconnected extends React.Component<ILayoutComponentProperties, never> {

    visualChooserDialog: any

    rootRef: RefObject<HTMLDivElement>

    autoresizeOn: boolean

    constructor(props: ILayoutComponentProperties) {
        super(props);

        const methodsToBind = [
            'onVisualSelectorButtonClick',
            'onPrimaryButtonClick',
            'onCancelButtonClick',
            'onCancelButtonClick',
            'onSelectedGroupChange',
            'onVisualizationTypeChange',
            'resize'
        ];
        _.bindAll(this, ...methodsToBind);

        this.resize = _.debounce(this.resize, adhocSettings.DEFAULT_AUTORESIZE_LAG);

        this.rootRef = React.createRef();
        this.autoresizeOn = false;
    }

    componentWillUnmount() {
        if (this.visualChooserDialog) {
            this.visualChooserDialog.remove();
            this.visualChooserDialog = null;
        }

        this.setAutoresize(false);
    }

    onVisualSelectorButtonClick() {
        this.props.setVisualizationChooserVisibility(true);
    }

    onPrimaryButtonClick() {
        this.props.setVisualizationChooserVisibility(false);
    }

    onCancelButtonClick() {
        this.props.setVisualizationChooserVisibility(false);
    }

    onSelectedGroupChange(groupName: string) {
        this.props.changeSelectedGroup(groupName);
    }

    onVisualizationTypeChange(type: any) {
        this.props.changeSelectedVisualizationType(type.type);
        if (this.props.eventCallbacks.onVisualizationTypeChange) {
            this.props.eventCallbacks.onVisualizationTypeChange(type.type);
        }
    }

    setAutoresize(autoresize: boolean) {
        if (autoresize) {
            if (!this.autoresizeOn) {
                window.addEventListener('resize', this.resize);
                this.autoresizeOn = true;
            }
        } else if (this.autoresizeOn) {
            window.removeEventListener('resize', this.resize);
            this.autoresizeOn = false;
        }
    }

    resize() {
        if (this.rootRef.current && this.rootRef.current.parentElement) {
            const height = $(this.rootRef.current.parentElement).height() || adhocSettings.DEFAULT_HEIGHT;
            const width = $(this.rootRef.current.parentElement).width() || adhocSettings.DEFAULT_WIDTH;
            this.props.resize(width, height);
        }
    }

    render() {
        const {
            autoresize,
            adHocViewHeight,
            visualizationDescriptor,
            visualizationTypesRegistry,
            visualizationChooser,
            levelsLayout,
            deferredAction,
            titleOptions,
            isInDesignerMode
        } = this.props;

        const selectedVisualizationType = visualizationTypesRegistry.find((item: any): boolean => {
            return item.descriptor.name === visualizationDescriptor.name;
        });
        const hasMeasureInColumns = Boolean(levelsLayout.columns.find((item: any): boolean => {
            return item.kind === 'measure';
        }));
        const hasMeasureInRows = Boolean(levelsLayout.rows.find((item: any): boolean => {
            return item.kind === 'measure';
        }));
        const hasMeasureInLayout = hasMeasureInColumns || hasMeasureInRows;
        let showWrongLayoutMessage = false;
        if (isInDesignerMode === false && hasMeasureInLayout
            && (selectedVisualizationType && selectedVisualizationType.available === false)
        ) {
            showWrongLayoutMessage = true;
        }

        let canvasHeight = adHocViewHeight;

        let
            DataServiceComponent,
            VisualizationChooser,
            VisualizationChooserButton;

        if (showWrongLayoutMessage) {

            DataServiceComponent = (
                <AdHocMessageComponent
                    title={i18n['adhoc.invalid.layout.title']}
                    message={i18n['adhoc.invalid.layout.message']}
                />
            );

            // the deferred action should be resolved here since we do not create any Data Component
            deferredAction.resolve();

        } else if (levelsLayout.columns.length || levelsLayout.rows.length) {
            switch (visualizationDescriptor.datasetType) {
            case datasetTypes.MULTI_LEVEL:
                DataServiceComponent = (
                    <MultiLevelDataServiceComponent visualization={visualizationDescriptor.component} controlsDataLoading={!!visualizationDescriptor.controlsDataLoading} />
                );
                break;
            case datasetTypes.MULTI_AXIS:
                DataServiceComponent = (
                    <MultiAxisDataServiceComponent visualization={visualizationDescriptor.component} controlsDataLoading={!!visualizationDescriptor.controlsDataLoading} />
                );
                break;
            default:
                DataServiceComponent = (
                    <SampleDataServiceComponent visualization={visualizationDescriptor.component} controlsDataLoading={!!visualizationDescriptor.controlsDataLoading} />
                );
            }
        } else {
            DataServiceComponent = (
                <AdHocMessageComponent />
            );

            // the deferred action should be resolved here since we do not create any Data Component
            deferredAction.resolve();
        }

        if (visualizationChooser.enabled !== false) {
            const typesToExclude = ['TileMap', 'tilemap'];
            VisualizationChooser = (
                <VisualChooserComponent
                    selectedType={visualizationDescriptor.name}
                    typesToExclude={typesToExclude}
                    {...visualizationChooser}
                    onPrimaryButtonClick={this.onPrimaryButtonClick}
                    onCancelButtonClick={this.onCancelButtonClick}
                    onSelectedGroupChange={this.onSelectedGroupChange}
                    onVisualizationChange={this.onVisualizationTypeChange}
                />
            );
            VisualizationChooserButton = (
                <div className={`jr-mAdhoc-visualization-launcher jr jr-jAdhocLauncher ${titleOptions.showTitle ? '' : 'jr-isMinimized'}`}>
                    <button
                        className="jr-mButton jr-mButtonAction jr-mButtonOutline jr jr-jAdhocLauncherButton"
                        aria-label="chart options"
                        type="button"
                        onClick={this.onVisualSelectorButtonClick}
                    >
                        <span className={`jr-mButton-icon jr-mIcon jr jr-jAdhocLauncherIcon ${titleOptions.showTitle ? 'jr-chartColumn' : 'jr-meatball'}`} />
                    </button>
                </div>
            );
        } else {
            VisualizationChooser = '';
            VisualizationChooserButton = '';
        }

        if (titleOptions.showTitle) {
            const titleHeight = this.props.titleOptions.domNodeHeight;
            // we don't want to make the container for resource too small to render the actual content, so I'd like
            // to reduce it's size if the container initially was 3 times bigger than the title height
            if (titleHeight && titleHeight * 3 < canvasHeight) {
                // add one more pixel to the height of the title to avoid issue with sub-pixel sizes.
                canvasHeight -= (titleHeight + 1);
            }
        }

        const canvasClassNames = ['jr', 'jr-mVisualization-canvas', 'jr-jAdhocVisualizationContainer'];
        let canvasMarginTopClass = '';
        if (!titleOptions.showTitle && visualizationChooser.enabled) {
            // When we don't show the title and when the visualization button is visible we might consider to have
            // some top margin for canvas to fit the button.
            canvasMarginTopClass = 'jr-uTop-single';
            if (
                visualizationDescriptor.name === 'Table'
                || (visualizationDescriptor.name === 'Crosstab' && levelsLayout.columns.length === 0)
            ) {
                // But when all items (measures or fields) located in the rows only, we need to have bigger margin:
                canvasMarginTopClass = 'jr-uTop-singlehalf';
            }
        }
        if (canvasMarginTopClass) {
            canvasClassNames.push(canvasMarginTopClass);
        }

        this.setAutoresize(autoresize);

        return (
            <div ref={this.rootRef} className="jr-mAdhoc jr-mAdhocViewer jr" style={{ height: `${canvasHeight}px`, position: 'relative' }}>

                <div className="jr-jAdhocMessage" />

                <div className="jr-mAdhoc-visualization jr jr-jAdhocVizualization" style={{ height: `${canvasHeight}px` }}>

                    { VisualizationChooserButton }

                    <TitleComponent />

                    <div
                        className={canvasClassNames.join(' ')}
                        style={{ height: `${canvasHeight - ((isInDesignerMode || titleOptions.showTitle) ? 0 : 6)}px` }}
                    >
                        { DataServiceComponent }
                    </div>
                </div>

                { VisualizationChooser }
            </div>
        );
    }
}

const connectFn = connect(mapStateToProps, mapDispatchToProps);
const LayoutComponent = connectFn(LayoutComponentUnconnected);

export {
    LayoutComponent,
    LayoutComponentUnconnected,
    mapDispatchToProps,
    mapStateToProps
};
